import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { STAGE_LABELS, timeAgo, currency } from "@/lib/constants";
import SLABadge from "@/components/SLABadge";
import TrackerTable from "@/components/TrackerTable";
import { Scale, Zap, CheckCircle2, Search, Sparkles, Send, X, ClipboardCopy } from "lucide-react";

const TABS = [
  { id: "my_active", label: "My Active Reviews", icon: Scale },
  { id: "queued",    label: "Queued for Attorney", icon: Zap },
  { id: "executed",  label: "Executed by Me", icon: CheckCircle2 },
];

export default function AttorneyPortal() {
  const nav = useNavigate();
  const [tab, setTab] = useState("my_active");
  const [data, setData] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/portal/attorney"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load portal"); }
  };
  useEffect(() => { load(); }, []);

  const claim = async (id) => {
    try { await api.post("/portal/attorney/claim", { contract_id: id }); toast.success("Claimed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Claim failed"); }
  };

  if (!data) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  const rows = data[tab] || [];

  return (
    <div className="p-6 relative" data-testid="attorney-portal-page">
      <div className="mb-6 flex items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phase 6 · Attorney Portal</div>
          <h1 className="text-2xl font-bold text-slate-900">Legal Review Workspace</h1>
          <p className="text-sm text-slate-500 mt-1">
            Your assigned reviews, contracts queued for legal attention, and everything you've executed.
          </p>
        </div>
        <button onClick={() => setAiOpen(true)} data-testid="ap-ai-btn"
                className="ml-auto h-10 px-4 rounded-md bg-gradient-to-r from-[#005289] to-[#00779f] text-white text-sm font-semibold flex items-center gap-2 shadow-sm">
          <Sparkles className="w-4 h-4"/> AI Drafting Panel
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3 mb-5" data-testid="ap-kpis">
        <KPI icon={Scale} label="My Active" value={data.counts.my_active}/>
        <KPI icon={Zap} label="Queued" value={data.counts.queued} tone={data.counts.queued > 0 ? "warn" : "ok"}/>
        <KPI icon={CheckCircle2} label="Executed by Me" value={data.counts.executed}/>
        <KPI icon={Zap} label="Overdue in Active" value={data.counts.overdue} tone={data.counts.overdue > 0 ? "danger" : "ok"}/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = data.counts?.[t.id] ?? 0;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ap-tab-${t.id}`}
                    className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                      tab === t.id ? "border-[#005289] text-[#005289] font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>
              <Icon className="w-4 h-4"/> {t.label}
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${tab === t.id ? "bg-[#005289] text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div data-testid={`ap-panel-${tab}`}>
        <TrackerTable rows={rows} onChanged={load} testIdPrefix={`ap-${tab}`}
                      actionColumn={(c) => (
                        tab === "queued" ? (
                          <button onClick={() => claim(c.id)} data-testid={`ap-claim-${c.contract_number}`}
                                  className="h-7 px-3 text-xs font-semibold bg-[#005289] text-white rounded hover:opacity-90">
                            Claim
                          </button>
                        ) : null
                      )}/>
      </div>

      {aiOpen && <AIDraftingPanel onClose={() => setAiOpen(false)}/>}
    </div>
  );
}

function KPI({ icon: Icon, label, value, tone = "neutral" }) {
  const color = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase text-slate-500 font-bold tracking-wider"><Icon className="w-3 h-3"/> {label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function AIDraftingPanel({ onClose }) {
  const [query, setQuery] = useState("");
  const [ctx, setCtx] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);

  const ask = async () => {
    if (!query.trim()) return;
    setBusy(true); setAnswer("");
    try {
      const question = ctx ? `${ctx}\n\n${query}` : query;
      const { data } = await api.post("/ai/query", { question, scope: "all" });
      setAnswer(data.answer || data.response || "");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(", ") : (d ? JSON.stringify(d) : "AI request failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const searchRepo = async () => {
    if (!searchQ.trim()) return;
    try {
      const { data } = await api.get(`/portal/attorney/search?q=${encodeURIComponent(searchQ)}`);
      setResults(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="fixed inset-y-0 right-0 w-[520px] bg-white border-l border-slate-200 shadow-2xl z-40 flex flex-col" data-testid="ap-ai-panel">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#005289]"/>
        <div className="font-bold text-slate-900">AI Drafting Panel</div>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700" data-testid="ap-ai-close"><X className="w-4 h-4"/></button>
      </div>

      <div className="p-5 space-y-4 overflow-y-auto flex-1">
        {/* Repo search */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Repository Search</div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input data-testid="ap-search-input" value={searchQ} onChange={e => setSearchQ(e.target.value)}
                     onKeyDown={e => e.key === "Enter" && searchRepo()}
                     placeholder="Similar clauses, vendor, purpose..."
                     className="h-9 pl-9 pr-3 w-full border border-slate-200 rounded-md text-sm"/>
            </div>
            <button onClick={searchRepo} className="h-9 px-3 text-xs font-semibold border border-slate-200 rounded hover:bg-slate-50">Search</button>
          </div>
          {results.length > 0 && (
            <div className="mt-2 max-h-56 overflow-y-auto border border-slate-100 rounded-md" data-testid="ap-search-results">
              {results.slice(0, 10).map(r => (
                <div key={r.id} className="px-3 py-2 border-b border-slate-100 last:border-b-0 text-xs hover:bg-slate-50 cursor-pointer"
                     onClick={() => window.open(`/contract/${r.id}`, "_blank")}>
                  <div className="font-semibold text-slate-900">{r.vendor_legal_name} <span className="font-mono text-slate-400">#{r.contract_number}</span></div>
                  <div className="text-slate-500 truncate">{r.purpose || r.description || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI query */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Ask AI</div>
          <input data-testid="ap-ai-context" value={ctx} onChange={e => setCtx(e.target.value)}
                 placeholder="Optional context (e.g., vendor name, clause topic)"
                 className="h-9 px-3 w-full border border-slate-200 rounded-md text-sm mb-2"/>
          <textarea data-testid="ap-ai-query" rows={3} value={query} onChange={e => setQuery(e.target.value)}
                    placeholder="e.g., Draft an indemnification clause capping vendor liability at contract value..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"/>
          <div className="flex justify-end mt-2">
            <button onClick={ask} disabled={busy || !query.trim()} data-testid="ap-ai-send"
                    className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-60">
              <Send className="w-3 h-3"/> {busy ? "Thinking..." : "Ask"}
            </button>
          </div>
        </div>

        {answer && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 relative" data-testid="ap-ai-answer">
            <button onClick={() => copy(answer)} className="absolute top-2 right-2 text-slate-400 hover:text-slate-700" title="Copy">
              <ClipboardCopy className="w-3.5 h-3.5"/>
            </button>
            <pre className="text-xs whitespace-pre-wrap pr-6">{answer}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
